import { useMemo, useState, type SubmitEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { format, formatDistanceToNow } from 'date-fns';
import { AlertCircle, Archive, BellOff, Bot, CalendarDays, Eye, List, MoreHorizontal, Plus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useConfirm } from '@/hooks/useConfirm';
import {
  type CMState,
  type ChangeManagement,
  type ChangeType,
  type CreateCMRequest,
  type ListCMsFilters,
  type RiskLevel,
  useArchiveCM,
  useChangeManagements,
  useCMSettings,
  useCreateCM,
} from '@/hooks/useChangeManagements';
import { preferredContext, useAvailableContextsInOrg } from '@/hooks/useContexts';
import { useActiveContext } from '@/hooks/useActiveContext';
import CMDashboard from '@/components/CMDashboard';
import CMAgenda from '@/components/CMAgenda';
import CMCalendar from '@/components/CMCalendar';
import { useMaintenanceWindows } from '@/hooks/useMaintenanceWindows';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * ChangeManagement — list view. Spec §4 MS.
 *
 * 3-up filter row (State / Context / Type) plus Show-archived
 * checkbox. ResponsiveTable with row-actions kebab menu (View,
 * Archive). Create form is an AppSheet — multi-field form with
 * context picker + suppress-alerts checkbox.
 */

const STATE_STYLE: Record<CMState, string> = {
  draft: 'bg-muted text-muted-foreground',
  waiting_approval: 'bg-sev-3/20 text-foreground',
  approved: 'bg-sev-5/15 text-sev-5',
  rejected: 'bg-sev-1/15 text-sev-1',
  in_progress: 'bg-brand-soft text-brand',
  paused: 'bg-sev-3/30 text-foreground',
  verification: 'bg-sev-5/20 text-sev-5',
  completed: 'bg-sev-4/15 text-sev-4',
  cancelled: 'bg-muted text-muted-foreground',
  partially_completed: 'bg-sev-3/20 text-foreground',
};

function formatState(state: CMState): string {
  return state
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function ChangeManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const organizationId = user?.organizationId;

  const [filters, setFilters] = useState<ListCMsFilters>({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [formContextId, setFormContextId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSuppressAlerts, setFormSuppressAlerts] = useState(false);
  const [formStartTime, setFormStartTime] = useState('');
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [formExpectedEndTime, setFormExpectedEndTime] = useState('');
  const [formAutoStart, setFormAutoStart] = useState(false);
  const [formChangeType, setFormChangeType] = useState<ChangeType>('normal');
  const [formRiskLevel, setFormRiskLevel] = useState<RiskLevel | 'unset'>('unset');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: cms, isLoading, isError, error } = useChangeManagements(organizationId, filters);
  // Unfiltered, for the "needs your attention" dashboard (independent of the table filter).
  const { data: dashboardCms } = useChangeManagements(organizationId);
  const { data: maintenanceWindows } = useMaintenanceWindows();

  // Open the create flow pre-scheduled for a calendar day (defaults to 09:00 local).
  const handleScheduleDay = (day: Date) => {
    setFormStartTime(format(day, "yyyy-MM-dd'T'09:00"));
    setIsCreateOpen(true);
  };
  // This organization's contexts only. A change is created against this
  // organization, and the list below it filters changes that are already
  // scoped to it — another organization's context is a choice that creates a
  // rejected submit or an empty filter.
  const { data: contexts } = useAvailableContextsInOrg(organizationId);
  const { activeContextId } = useActiveContext();

  // Start on the context the user is already working in, leaving a
  // deliberate choice alone once made.
  // Set during render (guarded, so it settles in one pass) rather than in an
  // effect, which would paint the empty field first.
  const preferred = preferredContext(contexts, activeContextId);
  if (!formContextId && preferred) setFormContextId(preferred.id);

  const createMutation = useCreateCM();
  const archiveMutation = useArchiveCM();
  const { confirm, confirmDialog } = useConfirm();
  const { data: cmSettings } = useCMSettings();

  const contextLookup = useMemo(() => {
    const m = new Map<string, string>();
    contexts?.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [contexts]);

  const handleCreateSubmit = async (e?: SubmitEvent) => {
    e?.preventDefault();
    setSubmitError(null);

    if (!formContextId) {
      setSubmitError('Context is required');
      return;
    }
    const next: CreateCMRequest = {
      contextId: formContextId,
      title: formTitle,
      description: formDescription || undefined,
      suppressAlerts: formSuppressAlerts,
      startTime: formStartTime || undefined,
      expectedEndTime: formExpectedEndTime || undefined,
      autoStart: formAutoStart || undefined,
      changeType: formChangeType,
      riskLevel: formRiskLevel === 'unset' ? undefined : formRiskLevel,
    };
    try {
      await createMutation.mutateAsync(next);
      setIsCreateOpen(false);
      setFormContextId('');
      setFormTitle('');
      setFormDescription('');
      setFormSuppressAlerts(false);
      setFormStartTime('');
      setFormExpectedEndTime('');
      setFormAutoStart(false);
      setFormChangeType('normal');
      setFormRiskLevel('unset');
    } catch {
      setSubmitError('Failed to create change management.');
    }
  };

  const columns = useMemo<readonly ResponsiveTableColumn<ChangeManagement>[]>(
    () => [
      {
        key: 'title',
        label: 'Title',
        hideMobileLabel: true,
        render: (cm) => (
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to={`/change-management/${cm.id}`}
              className="font-medium hover:underline truncate"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {cm.title}
            </Link>
            {cm.isAutomated ? (
              <Badge variant="secondary" className="text-[10px] font-normal flex-shrink-0">
                <Bot className="w-3 h-3" />
                Automated
              </Badge>
            ) : null}
            {cm.suppressAlerts ? (
              <Badge
                className="bg-sev-3/20 text-foreground border-0 text-[10px] font-normal flex-shrink-0"
                title="Alerts suppressed"
              >
                <BellOff className="w-3 h-3" />
                Alerts off
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        key: 'state',
        label: 'State',
        render: (cm) => (
          <div className="flex flex-col gap-1 items-start">
            <Badge className={cn('border-0 font-normal', STATE_STYLE[cm.state])}>
              {formatState(cm.state)}
            </Badge>
            {cm.state === 'waiting_approval' && cm.approvalStatus && cm.approvalStatus.totalApprovers > 0 ? (
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {cm.approvalStatus.approvedCount}/{cm.approvalStatus.totalApprovers} approved
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'progress',
        label: 'Progress',
        hideAt: 'mobile',
        render: (cm) => {
          const p = cm.planProgress;
          if (!p || p.planCount === 0) {
            return <span className="text-muted-foreground text-xs">—</span>;
          }
          const pct = p.totalSteps > 0 ? Math.round((p.completedSteps / p.totalSteps) * 100) : 0;
          return (
            <div className="flex items-center gap-2 min-w-[7rem]">
              <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                <div
                  className={cn('h-full rounded-full', p.hasFailedPlan ? 'bg-sev-1' : 'bg-sev-4')}
                  style={{ width: `${String(pct)}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">
                {p.completedSteps}/{p.totalSteps}
              </span>
            </div>
          );
        },
      },
      {
        key: 'context',
        label: 'Context',
        hideAt: 'mobile',
        render: (cm) => contextLookup.get(cm.contextId) ?? cm.contextId,
      },
      {
        key: 'created',
        label: 'Created',
        hideAt: 'mobile',
        render: (cm) => (
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(cm.createdAt), { addSuffix: true })}
          </span>
        ),
      },
      {
        key: 'startTime',
        label: 'Scheduled start',
        hideAt: 'mobile',
        render: (cm) =>
          cm.startTime ? (
            <span className="text-xs">{new Date(cm.startTime).toLocaleString()}</span>
          ) : (
            <span className="text-muted-foreground text-xs">Not scheduled</span>
          ),
      },
    ],
    [contextLookup],
  );

  if (isError) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading change managements:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Change management"
        subtitle="Plan, approve, and track network changes across your org"
        primary={
          <Button onClick={() => { setIsCreateOpen(true); }}>
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">Create change</span>
            <span className="md:hidden">New</span>
          </Button>
        }
      />

      {user?.id ? <CMDashboard cms={dashboardCms ?? []} userId={user.id} /> : null}

      <CMAgenda cms={dashboardCms ?? []} />

      {/* List / Calendar view toggle */}
      <div className="flex items-center gap-1 mb-4">
        <Button
          variant={view === 'list' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => { setView('list'); }}
        >
          <List className="w-4 h-4" />
          List
        </Button>
        <Button
          variant={view === 'calendar' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => { setView('calendar'); }}
        >
          <CalendarDays className="w-4 h-4" />
          Calendar
        </Button>
      </div>

      {view === 'calendar' ? (
        <CMCalendar
          cms={dashboardCms ?? []}
          windows={maintenanceWindows ?? []}
          onScheduleDay={handleScheduleDay}
        />
      ) : (
      <>
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="cm-state-filter" className="text-xs">State</Label>
            <Select
              value={filters.state ?? 'all'}
              onValueChange={(v) => {
                setFilters({ ...filters, state: v === 'all' ? undefined : v });
              }}
            >
              <SelectTrigger id="cm-state-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="waiting_approval">Waiting approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="verification">Verification</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="partially_completed">Partially completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cm-context-filter" className="text-xs">Context</Label>
            <Select
              value={filters.contextId ?? 'all'}
              onValueChange={(v) => {
                setFilters({ ...filters, contextId: v === 'all' ? undefined : v });
              }}
            >
              <SelectTrigger id="cm-context-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All contexts</SelectItem>
                {contexts?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cm-type-filter" className="text-xs">Type</Label>
            <Select
              value={
                filters.isAutomated === undefined
                  ? 'all'
                  : filters.isAutomated
                    ? 'automated'
                    : 'manual'
              }
              onValueChange={(v) => {
                setFilters({
                  ...filters,
                  isAutomated: v === 'all' ? undefined : v === 'automated',
                });
              }}
            >
              <SelectTrigger id="cm-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="automated">Automated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-border-subtle">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={filters.includeArchived ?? false}
              onCheckedChange={(v) => {
                setFilters({ ...filters, includeArchived: v === true });
              }}
            />
            <span>Show archived</span>
          </label>
        </div>
      </div>

      <ResponsiveTable<ChangeManagement>
        columns={columns}
        rows={cms ?? []}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        onRowClick={(r) => { void navigate(`/change-management/${r.id}`); }}
        rowActions={(cm) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${cm.title}`}
                onClick={(e) => { e.stopPropagation(); }}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => { e.stopPropagation(); }}>
              <DropdownMenuItem asChild>
                <Link to={`/change-management/${cm.id}`} className="gap-2 cursor-pointer">
                  <Eye className="w-4 h-4" />
                  View
                </Link>
              </DropdownMenuItem>
              {!cm.archivedAt ? (
                <DropdownMenuItem
                  onClick={() => {
                    void (async () => {
                      const ok = await confirm({
                        title: 'Archive change',
                        message: `Archive "${cm.title}"?`,
                        confirmText: 'Archive',
                        isDanger: true,
                      });
                      if (ok) archiveMutation.mutate(cm.id);
                    })();
                  }}
                  className="gap-2 text-destructive focus:text-destructive"
                >
                  <Archive className="w-4 h-4" />
                  Archive
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={
          <EmptyState
            variant="no-data"
            title="No change managements yet"
            description="Plan a coordinated network change to get started."
            action={
              <Button onClick={() => { setIsCreateOpen(true); }}>
                <Plus className="w-4 h-4" />
                Create change
              </Button>
            }
          />
        }
      />
      </>
      )}

      {/* Create modal */}
      <AppSheet
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        title="Create change management"
      >
        <AppSheet.Body>
          <form
            id="create-cm-form"
            onSubmit={(e) => { void handleCreateSubmit(e); }}
            className="space-y-4"
          >
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="cm-context">
                Context <span className="text-destructive">*</span>
              </Label>
              <Select value={formContextId} onValueChange={setFormContextId}>
                <SelectTrigger id="cm-context">
                  <SelectValue placeholder="Select a context" />
                </SelectTrigger>
                <SelectContent>
                  {contexts?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The context defines the scope and teams for this change.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cm-title"
                value={formTitle}
                onChange={(e) => { setFormTitle(e.target.value); }}
                placeholder="e.g., Router firmware upgrade — Building A"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-description">Description</Label>
              <Textarea
                id="cm-description"
                value={formDescription}
                onChange={(e) => { setFormDescription(e.target.value); }}
                placeholder="Detailed description of the change, expected impact, and rollback plan…"
                rows={5}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Change type</Label>
                <Select value={formChangeType} onValueChange={(v) => { setFormChangeType(v as ChangeType); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (pre-approved)</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Risk level</Label>
                <Select value={formRiskLevel} onValueChange={(v) => { setFormRiskLevel(v as RiskLevel | 'unset'); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">Not set</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cm-start">Scheduled start</Label>
                <Input
                  id="cm-start"
                  type="datetime-local"
                  value={formStartTime}
                  onChange={(e) => { setFormStartTime(e.target.value); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cm-end">Expected end</Label>
                <Input
                  id="cm-end"
                  type="datetime-local"
                  value={formExpectedEndTime}
                  onChange={(e) => { setFormExpectedEndTime(e.target.value); }}
                />
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer pt-1">
              <Checkbox
                checked={formSuppressAlerts}
                onCheckedChange={(v) => { setFormSuppressAlerts(v === true); }}
                className="mt-0.5"
              />
              <span>
                Suppress alerts during this change
                <span className="block text-xs text-muted-foreground mt-0.5">
                  Alerts will be temporarily suppressed for affected devices.
                </span>
              </span>
            </label>

            {cmSettings?.autoStartEnabled ? (
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={formAutoStart}
                  onCheckedChange={(v) => { setFormAutoStart(v === true); }}
                  className="mt-0.5"
                />
                <span>
                  Start automatically at the scheduled time
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Once approved, this change begins on its own at the start time. Set a
                    start time above.
                  </span>
                </span>
              </label>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsCreateOpen(false); }}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-cm-form"
            disabled={createMutation.isPending || !formTitle || !formContextId}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>
      {confirmDialog}
    </PageContainer>
  );
}
